import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useCurrentContext } from './useCurrentContext';
import { selectRailsSandboxesHash } from 'SandboxModule/selectors';
import ApiClientManager, { isParty, isProd } from 'core/apiClient';
import { RealmTypes } from 'RealmModule/constants/realm';
import { checkIsMosaicSandboxTeam } from 'SandboxModule/utils';

/**
 * Context for feature flag evaluation.
 * Derived from URL params or auth defaults.
 */
export interface FeatureFlagContext {
  tenant: string | undefined;
  realmId: string | undefined;
  realmType: string | undefined;
  isUsingNonDefaultRealm: boolean;
  isSubscriptionRealmSelected: boolean;
  isSandboxRealmSelected: boolean;
}

/**
 * Hook that provides feature flags based on current context (URL params or auth defaults).
 *
 * This replaces direct ApiClientManager calls in feature.ts with context-aware evaluation.
 *
 * @example
 * ```tsx
 * const { canCreateNewTeam, canCreateNewSandbox } = useFeatureFlags();
 * if (canCreateNewTeam) {
 *   // Show create team button
 * }
 * ```
 */
export const useFeatureFlags = () => {
  const { tenant, realmId } = useCurrentContext();
  const sandboxHash = useSelector(selectRailsSandboxesHash);

  const context = useMemo((): FeatureFlagContext => {
    const defaultRealmId = ApiClientManager.getDefaultRealm().realm_id;

    // Guard: when realmId is undefined, we're on the default realm
    const isUsingNonDefaultRealm = realmId != null && realmId !== defaultRealmId;

    // Look up realm from Redux; fall back to ApiClientManager's realm if
    // sandboxHash hasn't loaded yet (avoids race condition on initial render)
    const realm = realmId ? sandboxHash[realmId] : undefined;
    const realmType =
      realm?.realm_type ??
      (realmId ? ApiClientManager.getAuthDefaultRealm()?.realm_type : undefined);
    const isSubscriptionRealmSelected = realmType === RealmTypes.subscription;
    const isSandboxRealmSelected = realmType === RealmTypes.sandbox;

    return {
      tenant,
      realmId,
      realmType,
      isUsingNonDefaultRealm,
      isSubscriptionRealmSelected,
      isSandboxRealmSelected,
    };
  }, [tenant, realmId, sandboxHash]);

  return useMemo(
    () => ({
      /**
       * Whether user can create a new team.
       * On prod/party: Only on default realm or subscription realms.
       */
      canCreateNewTeam: getCanCreateNewTeamWithContext(context),

      /**
       * Whether user can create a new sandbox.
       * On prod: Only on default realm.
       * On party: On default realm or sandbox realms (not subscription).
       */
      canCreateNewSandbox: getCanCreateNewSandboxWithContext(context),

      /**
       * Whether bulk set user password is enabled.
       * Only available on sandbox realms.
       */
      isBulkSetUserPasswordEnabled: getIsBulkSetUserPasswordEnabledWithContext(context),

      /**
       * Whether export member CSV is enabled.
       * Only available on sandbox realms.
       */
      isExportMemberCsvEnabled: getIsExportMemberCsvEnabledWithContext(context),

      /**
       * Whether dev tools tab is enabled.
       */
      isDevToolTabEnabled: getIsDevToolTabEnabledWithContext(context),

      /**
       * Whether to show sandbox tab.
       */
      canSeeSandboxTab: isParty || isProd,

      /**
       * Whether to show the top bar shortcut to the shared demo account.
       * Prod only: the account it points at only exists there.
       */
      canJumpToDemoAccount: isProd,

      /**
       * Whether integration server connection should be enabled.
       */
      shouldEnableIntegrationServerConnection:
        getShouldEnableIntegrationServerConnectionWithContext(context),

      /**
       * Whether to show subphase conversion status.
       */
      shouldShowSubphaseConversionStatus: getShouldShowSubphaseConversionStatusWithContext(context),

      /**
       * Raw context for custom flag evaluation.
       */
      context,
    }),
    [context],
  );
};

/* -------------------------------------------------------------------------- */
/*                       Context-Aware Feature Functions                       */
/* -------------------------------------------------------------------------- */

/**
 * Check if user can create a new team with explicit context.
 */
export const getCanCreateNewTeamWithContext = (ctx: FeatureFlagContext) => {
  if (isProd || isParty) {
    return (
      !ctx.isUsingNonDefaultRealm || (ctx.isUsingNonDefaultRealm && ctx.isSubscriptionRealmSelected)
    );
  }
  return true;
};

/**
 * Check if user can create a new sandbox with explicit context.
 */
export const getCanCreateNewSandboxWithContext = (ctx: FeatureFlagContext) => {
  if (isProd) {
    return !ctx.isUsingNonDefaultRealm;
  }
  if (isParty) {
    return (
      !ctx.isUsingNonDefaultRealm ||
      (ctx.isUsingNonDefaultRealm && !ctx.isSubscriptionRealmSelected)
    );
  }
  return true;
};

/**
 * Enabled only inside a non-default, non-subscription realm (and never outside prod/party).
 */
const isNonSubscriptionRealmOnlyFeatureEnabled = (ctx: FeatureFlagContext) => {
  if (isProd) {
    const isDisabled =
      !ctx.isUsingNonDefaultRealm ||
      (ctx.isUsingNonDefaultRealm && ctx.isSubscriptionRealmSelected);
    return !isDisabled;
  }
  if (isParty) {
    return ctx.isUsingNonDefaultRealm && !ctx.isSubscriptionRealmSelected;
  }
  return false;
};

/**
 * Check if bulk set user password is enabled with explicit context.
 */
export const getIsBulkSetUserPasswordEnabledWithContext = isNonSubscriptionRealmOnlyFeatureEnabled;

/**
 * Check if export member CSV is enabled with explicit context.
 */
export const getIsExportMemberCsvEnabledWithContext = isNonSubscriptionRealmOnlyFeatureEnabled;

/**
 * Check if dev tools tab is enabled with explicit context.
 */
export const getIsDevToolTabEnabledWithContext = (ctx: FeatureFlagContext) => {
  if (isParty) {
    return (
      !ctx.isUsingNonDefaultRealm || (ctx.isUsingNonDefaultRealm && ctx.isSubscriptionRealmSelected)
    );
  }
  return true;
};

/**
 * Check if integration server connection should be enabled with explicit context.
 */
export const getShouldEnableIntegrationServerConnectionWithContext = (ctx: FeatureFlagContext) => {
  if (isProd || isParty) {
    return ctx.isSubscriptionRealmSelected || !ctx.isUsingNonDefaultRealm;
  }
  return true;
};

/**
 * Check if subphase conversion status should be shown with explicit context.
 */
export const getShouldShowSubphaseConversionStatusWithContext = (ctx: FeatureFlagContext) => {
  if (ctx.isSandboxRealmSelected) {
    return !ctx.isUsingNonDefaultRealm;
  }
  return true;
};

/**
 * Check if editing member email is disabled with explicit context.
 */
export const getIsEditingMemberEmailDisabledWithContext = (
  ctx: FeatureFlagContext & { teamId: number },
) => {
  if (isProd) {
    const isMosaicSandboxTeam = checkIsMosaicSandboxTeam(ctx.teamId);
    return isMosaicSandboxTeam && ctx.tenant === 'try';
  }
  return !isParty;
};
